import { useEffect, useState, useRef } from "react";
import { AnalyticsOptionsHeader } from "./analytics-options-header";
import { fetchAdditionalHoursUsingAnalyticsSearchOptions } from '../../reducers/approvalSlice';
import { QualityAlert } from "../../models/quality-alert";
import { DailyChecklist, ProactiveAudit } from "../../models/daily-checklist";
import { AdditionalHours } from "../../models/additional-hours";
import {
  startOfWeek,
  startOfMonth,
  addDays,
  differenceInDays,
  format,
} from "date-fns";
import { ChartData, Chart, registerables } from "chart.js";
import { Line, Bar, Pie } from "react-chartjs-2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpFromBracket } from "@fortawesome/free-solid-svg-icons";
import { Supplier } from "../../models/supplier";
import { useAppDispatch, useAppSelector } from "../../reducers/store";
import { getDailyChecklistUsingAnalyticsSearchOptions } from "../../reducers/analyticsSlice";
import { getQualityAlertsUsingAnalyticsSearchOptions } from "../../reducers/qualityAlertSlice";
import { calculateDateRange } from "../../reducers/sessionSlice";

Chart.register(...registerables);

const monthMap: { [key: string]: string } = {
  "01": "January",
  "02": "February",
  "03": "March",
  "04": "April",
  "05": "May",
  "06": "June",
  "07": "July",
  "08": "August",
  "09": "September",
  "10": "October",
  "11": "November",
  "12": "December",
};

export type AnalyticsSearchOptions = {
  type: string;
  group: string;
  calculation: string;
  timeIncrement: string;
  chartType: string;
  timePeriod: string;
  customStartDate: string | null;
  customEndDate: string | null;
};

export type AnalyticsGrouping = {
  parent: {
    id: number;
    name: string;
  };
  increment: string;
  children: any[];
  displayChildren: {
    date: Date | string;
    displayString: string;
    children: any[];
  }[];
  calculation: string;
};

interface DateRange {
  after: Date;
  before: Date;
}

export function Analytics() {
  const selectedSupplier = useAppSelector((state) => state.supplier.selectedSupplier);
  const selectedProducingFacilities = useAppSelector((state) => state.supplier.selectedProducingFacilities);
  const selectedProducingFacilitiesFiltered = useAppSelector((state) => state.supplier.selectedProducingFacilitiesFiltered);
  const initialLoadComplete = useAppSelector((state) => state.supplier.initialLoadComplete);

  const dispatch = useAppDispatch();

  const exportTableToCSV = () => {
    // Extract dates dynamically from the grouped data
    const dateHeaders =
      groupedData[0]?.displayChildren.map((child) => child.displayString) ?? [];

    const headers = [
      "Name",
      searchOptions.calculation === "Percentage" ||
      searchOptions.calculation === "Average"
        ? "Row " + searchOptions.calculation
        : "Row Total",
      ...dateHeaders,
    ];

    const rows = groupedData.map((data) => {
      // Insert line breaks and wrap in quotes to keep it within a single cell
      const name = `"${(data.parent?.name ?? "All").replace(/(.{30})/g, "$1\n").trim()}"`;

      const value =
        data.calculation === "Average"
          ? (data.displayChildren.length > 0
              ? data.children.length / data.displayChildren.length
              : 0
            ).toFixed(2)
          : data.calculation === "Percentage"
            ? `${data.children.length} (${(
                (data.children.length / data.displayChildren.length) *
                100
              ).toFixed(2)}%)`
            : (data.children.length ?? 0);

      // Map each date's value in the row
      const dateValues = dateHeaders.map((date) => {
        const dateData = data?.displayChildren.find(
          (child) => child.displayString === date,
        );
        return data.calculation === "Percentage"
          ? dateData && data.children.length > 0
            ? (
                ((dateData.children.length ?? 0) / data.children.length) *
                100
              ).toFixed(2) + "%"
            : "0%"
          : (dateData?.children?.length ?? 0);
      });

      // Combine name, value, and date values for each row
      return [name, value, ...dateValues];
    });

    // Generate CSV content
    let csvContent =
      "data:text/csv;charset=utf-8," +
      headers.join(",") +
      "\n" +
      rows.map((e) => e.join(",")).join("\n");

    // Create a link to download the CSV
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "table_export.csv");
    document.body.appendChild(link); // Required for Firefox
    link.click();
    document.body.removeChild(link);
  };

  const chartRef = useRef<any>(null);

  // Default is triggering api call before actual call is made
  const defaultOptions: AnalyticsSearchOptions = {
    type: "Quality Alerts",
    group: "Producing Facility",
    calculation: "Total",
    timeIncrement: "Days",
    chartType: "Area",
    timePeriod: "30d",
    customStartDate: null,
    customEndDate: null,
  };

  const [lastSearchOptions, setLastSearchOptions] =
    useState<AnalyticsSearchOptions>(defaultOptions);
  const [searchOptions, setSearchOptions] =
    useState<AnalyticsSearchOptions>(defaultOptions);

  const [groupedData, setGroupedData] = useState<AnalyticsGrouping[]>([]);

  const [loading, setLoading] = useState<boolean>(false);

  const chartOptions = {
    responsive: true,
    aspectRatio: 5,
    plugins: {
      legend: {
        position: "bottom",
      },
    },
    interaction: {
      intersect: false,
      mode: 'nearest',
    },
    tension: 0,
    scales: {
      x: {
        title: {
          display: true,
          text: "Dates",
          font: {
            size: 14,
            weight: "bold"
          }
        },
      },
      y: {
        title: {
          display: true, // Show the label on the y-axis
          text:
            searchOptions.calculation === "Percentage"
              ? `${searchOptions.calculation} %`
              : searchOptions.calculation,
          font: {
            size: 14,
            weight: "bold"
          }
        },
      },
    },
  };
  const pieChartOptions = {
    responsive: true,
    aspectRatio: 5,
    plugins: {
      legend: {
        position: "bottom",
      },
    },
  };

  const [chartData, setChartData] = useState<ChartData | any>(null);
  const [totalChartData, setTotalChartData] = useState<ChartData | any>(null);
  const [searchText, setSearchText] = useState<string>("");

  useEffect(() => {
    if (
      searchOptions &&
      JSON.stringify(lastSearchOptions) !== JSON.stringify(searchOptions)
    ) {
      processRequest();
    }

    setLastSearchOptions(searchOptions);
  }, [searchOptions]);

  const processRequest = async () => {
    setLoading(true);
    var data: any[] = [];
    
    try {
      // Makes api call based on type of search
      switch (searchOptions.type) {
        case "Quality Alerts":
          data = await processQualityAlert();
          break;
        case "Near Misses": 
          data = await processQualityAlert();
          break;
        case "All Quality Issues":
          data = await processQualityAlert();
          break;
        case "Proactive Audits":
          data = await processProactiveAudits();
          break;
        case "Additional Hours":
          data = await processAdditionalHours();
          break;
        default:
          break;
      }

      const groupingResult = processGrouping(data);

      const dateRange = await dispatch(calculateDateRange(
        searchOptions.timePeriod,
      ));

      if (dateRange.payload) {
        const range = dateRange.payload as DateRange;
        if (searchOptions.customStartDate && searchOptions.customEndDate) {
          range.after = new Date(searchOptions.customStartDate);
          range.before = new Date(searchOptions.customEndDate);
        }

        const seperatedGroupResults = seperateGroupResultsByTimeIncrement(
          groupingResult,
          range.after,
          range.before,
        );

        setGroupedData(seperatedGroupResults);
        generateChartData(seperatedGroupResults);
      }
    } catch (error) {
      console.error('Error processing request:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateChartData = (data: AnalyticsGrouping[]) => {
    const labels = data[0]?.displayChildren?.map((d) => d.displayString) ?? [];

    const datasets = data.map((d) => {
      const totalChildren = d.displayChildren.reduce(
        (sum, child) => sum + child.children.length,
        0,
      );
      const dataValues = d.displayChildren.map((child) => {
        const value = child.children.length;
        return searchOptions.calculation === "Percentage"
          ? (value / totalChildren) * 100
          : value;
      });

      return {
        label: d?.parent?.name?.trim() ? d.parent.name : "All",
        data: dataValues,
        borderWidth: 1,
        fill: searchOptions.chartType === "Area" ? "origin" : false,
      };
    });

    const totalChildrenAll = data.reduce(
      (sum, d) => sum + d.children.length,
      0,
    );
    const totalDataValues = data.map((d) => {
      const value = d.children.length;
      return searchOptions.calculation === "Percentage"
        ? (value / totalChildrenAll) * 100
        : value;
    });

    const totalChartData = {
      labels: data.map((d) => d.parent?.name ?? "All"),
      datasets: [
        {
          data: totalDataValues,
        },
      ],
    };

    setChartData({
      labels,
      datasets,
    });

    setTotalChartData(totalChartData);
  };

  // This function includes all necessary dates on each grouping
  const seperateGroupResultsByTimeIncrement = (
    data: AnalyticsGrouping[],
    startDate: Date,
    endDate: Date,
  ) => {
    // Sort data by time
    data.forEach((d) => {
      d.children = d.children.sort((a, b) => {
        return (
          new Date(a.time ?? a.createTime).getTime() -
          new Date(b.time ?? b.createTime).getTime()
        );
      });
    });

    // Set up display children seperated by time
    const timeBetweenDays = differenceInDays(endDate, startDate) ?? 0;

    for (let i = 0; i < timeBetweenDays; i++) {
      data.forEach((d) => {
        var date = addDays(startDate, i);

        switch (searchOptions?.timeIncrement) {
          case "Days":
            break;
          case "Weeks":
            date = startOfWeek(date);
            break;
          case "Months":
            date = startOfMonth(date);
            break;
          case "Years":
            date = new Date(date.getFullYear(), 0, 1);
            break;
          default:
            break;
        }

        if (
          !d.displayChildren.find(
            (d: any) => d.date?.getTime() === date?.getTime(),
          )
        ) {
          d.displayChildren.push({
            date,
            displayString:
              searchOptions?.timeIncrement === "Years"
                ? format(date, "yyyy")
                : format(date, "MM/dd"),
            children: [],
          });
        }
      });
    }

    data
      .flatMap((d) => d.children)
      .forEach((d: any) => {
        var date = new Date(d.time ?? d.createTime);

        switch (searchOptions?.timeIncrement) {
          case "Days":
            break;
          case "Weeks":
            date = startOfWeek(date);
            break;
          case "Months":
            date = startOfMonth(date);
            break;
          case "Years":
            date = new Date(date.getFullYear(), 0, 1);
            break;
          default:
            break;
        }

        d.tableDate = date;
      });

    data.forEach((d) => {
      d.children.forEach((c: any) => {
        if (c.tableDate) {
          var existing = d.displayChildren.find(
            (a: any) =>
              format(a.date, "yyyy-MM-dd") ===
              format(c.tableDate, "yyyy-MM-dd"),
          );

          if (existing) {
            existing.children.push(c);
          } else {
            d.displayChildren.push({
              date: c.tableDate,
              displayString: format(c.tableDate, "MM/dd"),
              children: [c],
            });
          }
        }
      });
    });

    return data;
  };

  const processQualityAlert = async () => {
    var qualityAlerts: QualityAlert[] = [];

    if (searchOptions) {
      // The difference between this call is set inside the service function
      const result = await dispatch(getQualityAlertsUsingAnalyticsSearchOptions({
        options: searchOptions
      }));

      if (result.payload) {
        qualityAlerts = result.payload as QualityAlert[];
      }

      if (selectedSupplier && selectedSupplier.id) {
        qualityAlerts = qualityAlerts.filter((q) =>
          [selectedSupplier, ...(selectedProducingFacilitiesFiltered ?? [])]
            .map((s) => s?.id)
            .includes((q.supplier as Supplier)?.id),
        );
      }
    }

    return qualityAlerts;
  };

  const processProactiveAudits = async () => {
    var dailyChecklists: DailyChecklist[] = [];
    var audits: ProactiveAudit[] = [];

    if (searchOptions) {
      const result = await dispatch(getDailyChecklistUsingAnalyticsSearchOptions(searchOptions));
      
      if (result.payload) {
        dailyChecklists = result.payload as DailyChecklist[];
        // Convert daily checklist to audits
        audits = dailyChecklists
          .map((d) =>
            d.audits.map((a) => {
              return { ...a, location: d.location };
            }),
          )
          .flat();
        if (selectedSupplier && selectedSupplier.id) {
          audits = audits.filter((a) =>
            [selectedSupplier, ...(selectedProducingFacilitiesFiltered ?? [])]
              .map((s) => s?.id)
              .includes((a.supplier as Supplier)?.id),
          );
        }
      }
    }

    return audits;
  };

  const processAdditionalHours = async () => {
    var additionalHours: AdditionalHours[] = [];

    if (searchOptions) {
      const result = await dispatch(fetchAdditionalHoursUsingAnalyticsSearchOptions(searchOptions));
      
      if (result.payload) {
        additionalHours = result.payload as AdditionalHours[];

        if (selectedSupplier && selectedSupplier.id) {
          additionalHours = additionalHours.filter((a) =>
            [selectedSupplier, ...(selectedProducingFacilitiesFiltered ?? [])]
              .map((s) => s?.id)
              .includes((a.supplier as Supplier)?.id),
          );
        }
      }
    }

    return additionalHours;
  };

  const processGrouping = (data: any[]) => {
    var nameField = ["name"];
    var groupField = "supplier";
    switch (searchOptions.group) {
      case "Producing Facility":
        groupField = "supplier";

        break;
      case "Part Number":
        return processPartNumberMapping(data);
      case "Location":
        groupField = "location";

        break;
      case "Representative":
        groupField = "createUser";
        nameField = ["firstName", "lastName"];

        break;
      case "OEM":
        groupField = "location.oem";

        break;
      case "Issue Type": 
        return processIssueTypeMapping(data);
        break;
      case "No Grouping":
        return [
          { parent: null, children: data, displayChildren: [], calculation: 0 },
        ] as any as AnalyticsGrouping[];
      default:
        break;
    }

    var groupingMap = new Map([
      ["Quality Alerts", groupField],
      ["All Quality Issues", groupField],
      ["Near Misses", groupField],
      ["Proactive Audits", groupField],
      ["Additional Hours", groupField],
    ]);

    return processGroupMapping(data, groupingMap, nameField);
  };

  const processPartNumberMapping = (data: any[]): AnalyticsGrouping[] => {
    const grouping: AnalyticsGrouping[] = [];

    for (let i = 0; i < data.length; i++) {
      const element = data[i];

      // Case 1: If data.part exists, use part number directly
      if (element.part && element.part.number) {
        const partNumber = element.part.number;

        // Check if part number is already in grouping
        const existingGrouping = grouping.find(
          (g) => g.parent?.id === partNumber,
        );

        if (!existingGrouping) {
          // Create a new group for this part number
          grouping.push({
            parent: {
              id: partNumber, // Use part number as ID
              name: partNumber, // Use part number as name
            },
            increment: searchOptions.timeIncrement,
            children: [element], // Add the current item as a child
            displayChildren: [],
            calculation: searchOptions.calculation
              ? searchOptions.calculation
              : "Total",
          });
        } else {
          // If group already exists, add the item to its children
          existingGrouping.children.push(element);
        }
      } else {
        // Case 2: If data.part does not exist, follow the normal qualityAlertLocationInfo process
        // Check if qualityAlertLocationInfo is an array before iterating
        if (Array.isArray(element.qualityAlertLocationInfo)) {
          // Loop through each qualityAlertLocationInfo directly
          for (let location of element.qualityAlertLocationInfo) {
            // Now directly loop through qualityAlertDiscrepantMaterial
            if (Array.isArray(location.qualityAlertDiscrepantMaterial)) {
              // Loop through each qualityAlertDiscrepantMaterial in this location
              for (let discrepantMaterial of location.qualityAlertDiscrepantMaterial) {
                // Check if part and part.number exist
                if (discrepantMaterial.part && discrepantMaterial.part.number) {
                  const partNumber = discrepantMaterial.part.number;

                  // Check if part number is already in grouping
                  const existingGrouping = grouping.find(
                    (g) => g.parent?.id === partNumber,
                  );

                  if (!existingGrouping) {
                    // Create a new group for this part number
                    grouping.push({
                      parent: {
                        id: partNumber, // Use part number as ID
                        name: partNumber, // Use part number as name
                      },
                      increment: searchOptions.timeIncrement,
                      children: [element], // Add the current item as a child
                      displayChildren: [],
                      calculation: searchOptions.calculation
                        ? searchOptions.calculation
                        : "Total",
                    });
                  } else {
                    // If group already exists, add the item to its children
                    existingGrouping.children.push(element);
                  }
                } else {
                  console.log(
                    "No valid part number found for discrepantMaterial",
                  );
                }
              }
            } else {
              console.log(
                "qualityAlertDiscrepantMaterial is not iterable or is missing",
              );
            }
          }
        } else {
          console.log(
            "qualityAlertLocationInfo is not iterable or is missing for element",
            element,
          );
        }
      }
    }
    return grouping;
  };


  const processIssueTypeMapping = (data: any[]): AnalyticsGrouping[] => {
    const grouping: AnalyticsGrouping[] = [];

    for (let i = 0; i < data.length; i++) {
      const element = data[i];
      const issueType = element.level === 0 ? "Near Miss" : "Quality Alert";
      const existingGrouping = grouping.find(
        (g) => g.parent?.name === issueType,
      );

      if (!existingGrouping) {
        grouping.push({
          parent: {
            id: element.level,
            name: issueType,
          },
          increment: searchOptions.timeIncrement,
          children: [element],
          displayChildren: [],
          calculation: searchOptions.calculation
        })
      } else {
        existingGrouping.children.push(element);
      }
    }

    return grouping;
  }
  const processGroupMapping = (
    data: any[],
    typeMap: Map<string, string> = new Map(),
    nameFields: string[] = ["name"],
  ) => {
    const grouping: AnalyticsGrouping[] = [];

    for (let i = 0; i < data.length; i++) {
      const element = data[i];
      // Get supplier element from data
      const nestedValue: any = getNestedAttributeOnObject(
        element,
        typeMap.get(searchOptions.type) ?? "supplier",
      );

      // Check if supplier is already in grouping
      const existingGrouping = grouping.find(
        (g) => g.parent?.id === nestedValue?.id,
      );

      if (!existingGrouping) {
        grouping.push({
          parent: {
            id: nestedValue.id,
            name:
              nameFields.map((n) => nestedValue[n]).join(" ") ??
              nestedValue[nameFields[0]],
          },
          increment: searchOptions.timeIncrement,
          children: [element],
          displayChildren: [],
          calculation: searchOptions.calculation
            ? searchOptions.calculation
            : "Total",
        });
      } else {
        existingGrouping.children.push(element);
      }
    }

    return grouping;
  };

  const getNestedAttributeOnObject = (obj: any, path: string) => {
    return path.split(".").reduce((o, i) => o[i], obj);
  };

  function formatMonth(date: string) {
    let str = date;
    let stringDate = str.split("/")[0];

    let result = monthMap[stringDate];

    return result;
  }

  return (
    <>
      <div className="relative top-[20-px] mx-auto flex h-[calc(100vh-150px)] w-[90%] flex-col overflow-auto rounded-[8px] bg-[white] p-[40px] text-[#212322]">
        <AnalyticsOptionsHeader
          chartRef={chartRef}
          searchOptions={searchOptions}
          setSearchOptions={setSearchOptions}
          processRequest={processRequest}
        />

        {loading && (
          <div className="flex h-[100%] w-[100%] items-center justify-center pt-[20px]">
            <span className="loading loading-spinner h-[40px] w-[40px] text-tqsYellow"></span>
          </div>
        )}

        {!loading && chartData && (
          <div className="max-h-[400px] w-full pt-[20px]">
            {(() => {
              switch (searchOptions.chartType) {
                case "Line":
                  return (
                    <Line
                      ref={chartRef}
                      height={200}
                      data={chartData}
                      options={chartOptions as any}
                    />
                  );
                case "Area":
                  return (
                    <Line
                      ref={chartRef}
                      height={200}
                      data={chartData}
                      options={chartOptions as any}
                    />
                  );
                case "Pie":
                  return (
                    <Pie
                      ref={chartRef}
                      height={200}
                      data={totalChartData}
                      options={pieChartOptions as any}
                    />
                  );
                case "Bar":
                  return (
                    <Bar
                      ref={chartRef}
                      height={200}
                      data={chartData}
                      options={chartOptions as any}
                    />
                  );
              }
            })()}
          </div>
        )}

        {!loading && searchOptions.chartType === "Number" && (
          <div className="flex h-[50%] w-[100%] flex-col items-center justify-center pb-[20px] pt-[20px]">
            <span className="text-[20px] font-semibold">Total Count</span>
            <span className="text-[40px] font-semibold">
              {groupedData
                .map((d) =>
                  d.displayChildren
                    .map((x) => x.children.length)
                    .reduce((a, b) => a + b, 0),
                )
                .reduce((a, b) => a + b, 0)}
            </span>
          </div>
        )}

        {!loading && searchOptions.chartType !== "Number" && (
          <div className="flex h-[100%] w-[100%] flex-col pb-[20px] pt-[20px]">
            {/* Table Search Bar */}
            <div className="flex flex-row items-center justify-between pb-[14px]">
              <div className="flex flex-row items-center"></div>

              <div className="flex w-[45%] flex-row items-center gap-x-[10px]">
                <input
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Search..."
                  className="h-[36px] w-[100%] rounded-[4px] border-[1px] border-[#dbe5eb] bg-[white] p-[8px] shadow-md"
                ></input>
                <button
                  className="flex h-[36px] items-center justify-evenly gap-x-[8px] rounded-md border border-gray-300 bg-white px-[12px] shadow-lg"
                  onClick={exportTableToCSV}
                >
                  <FontAwesomeIcon
                    className="text-[14px] text-[#212322]"
                    icon={faArrowUpFromBracket}
                  />
                  <span className="text-[14px] text-[#212322]">Export</span>
                </button>
              </div>
            </div>

            <div className="flex w-[100%] flex-row border-b-0 border-[#dbe5eb]">
              {/* Fixed section */}
              <div className="flex w-[50%] flex-col border-[#dbe5eb]">
                {/* Headers */}
                <div className="flex h-[48px] w-[100%] flex-row items-center justify-between border-b-[1px] border-r-[1px] border-t border-b-[#dbe5eb]">
                  <div className="flex h-[100%] w-[60%] items-center border-r-[1px] border-r-[#dbe5eb] p-[8px] text-[15px] font-bold text-[#212322]">
                    <span>Name</span>
                  </div>
                  <div className="flex h-[100%] w-[40%] items-center p-[8px] text-[15px] font-bold text-[#212322]">
                    <span>
                      {searchOptions.calculation === "Percentage" || "Average"
                        ? "Row " + " " + searchOptions.calculation
                        : "Row Total"}
                    </span>
                  </div>
                </div>

                {groupedData
                  .filter((g) =>
                    g.parent?.name
                      ? g.parent.name
                          .toLowerCase()
                          .includes(searchText?.toLowerCase())
                      : true,
                  )
                  .map((data, index) => (
                    <div
                      key={index}
                      className="flex h-[48px] w-[100%] flex-row items-center justify-between border-b-[1px] border-r-[1px] border-b-[#dbe5eb]"
                    >
                      <div className="flex h-[100%] w-[60%] items-center border-r-[1px] border-r-[#dbe5eb] p-[8px] text-[15px] font-medium text-[#212322]">
                        <span className="overflow-hidden text-ellipsis text-nowrap">
                          {data.parent?.name ?? "All"}{" "}
                        </span>
                      </div>
                      <div className="flex h-[100%] w-[40%] items-center p-[8px] text-[15px] font-medium text-[#212322]">
                        <span>
                          {data.calculation === "Average"
                            ? (data.displayChildren.length > 0
                                ? data.children.length /
                                  data.displayChildren.length
                                : 0
                              ).toFixed(2)
                            : data.calculation === "Percentage"
                              ? `${data.children.length} (${((data.children.length / data.children.length) * 100).toFixed(2)}%)`
                              : (data.children.length ?? 0)}
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
              {/* Scrollable section */}
              <div className="custom-scrollbar flex w-[50%] flex-col overflow-auto scrollbar">
                {/* Headers */}
                <div className="flex h-[48px] w-[100%] flex-row items-center">
                  {(groupedData[0]?.displayChildren ?? []).map(
                    (data, index) => (
                      <div
                        key={index}
                        className="flex h-[100%] w-[180px] min-w-[180px] items-center border-b-[1px] border-r-[1px] border-t border-b-[#dbe5eb] border-r-[#dbe5eb] p-[8px] text-[15px] font-bold text-[#212322]"
                      >
                        <span>
                          {searchOptions.timeIncrement === "Months"
                            ? formatMonth(data.displayString)
                            : data.displayString}
                        </span>
                      </div>
                    ),
                  )}
                </div>

                {groupedData
                  .filter((g) =>
                    g.parent?.name
                      ? g.parent.name
                          .toLowerCase()
                          .includes(searchText?.toLowerCase())
                      : true,
                  )
                  .map((data, index) => (
                    <div
                      key={index}
                      className="flex h-[48px] w-[100%] flex-row items-center"
                    >
                      {(data?.displayChildren ?? []).map(
                        (child, childIndex) => (
                          <div
                            key={childIndex}
                            className="flex h-[100%] w-[180px] min-w-[180px] items-center border-b-[1px] border-r-[1px] border-b-[#dbe5eb] border-r-[#dbe5eb] p-[8px] text-[15px] font-medium text-[#212322]"
                          >
                            <span>
                              {data.calculation === "Percentage"
                                ? (
                                    (child.children.length /
                                      data.children.length) *
                                    100
                                  ).toFixed(2) + "%"
                                : (child.children.length ?? 0)}
                            </span>
                          </div>
                        ),
                      )}
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
